import React, { FormEvent } from 'react'
import { Menu, MenuList, MenuButton, MenuLink } from '@reach/menu-button'
import cx from 'classnames'
import '@reach/menu-button/styles.css'

import './DropdownMenu.styles.scss'
import AuthService from '../../services/AuthService'
import { Session } from '../../models/Auth'
import { useSession } from '../../context/AuthProvider'
import Cookies from 'js-cookie'

export interface DropdownMenuProps {
  items?: Item[]
  buttonText?: React.ReactNode
  buttonArrow?: boolean
  buttonClassName?: string
  buttonCustomElement?: React.FC
  className?: string
  'data-testid'?: string,
  showImpersonationMenu?: boolean
}

export interface Item {
  as?: 'a' | 'button'
  text: string
  href?: string
  rel?: string
  target?: string
  onClick?: (event: React.MouseEvent<HTMLElement>) => void
  hidden?: boolean
  ariaLabel?: string
}

const DropdownMenu = ({
  items = [],
  className,
  'data-testid': dataTest = 'dropdown-menu',
  buttonText = 'Actions',
  buttonArrow = true,
  buttonClassName,
  buttonCustomElement,
  // TODO: a workaround as this dropdown menu is used for multiple purposes but the items collection does not permit arbitrary child elements.
  showImpersonationMenu = false,
}: DropdownMenuProps) => {
  const [{ user, auth }] = useSession();
  const session = {user, auth} as Session;
  return (
    <Menu className={cx('text-base', className)} data-testid={dataTest}>
      {buttonCustomElement ? (
        buttonCustomElement({ children: null }, 'hi')
      ) : (
        <MenuButton className={cx(buttonClassName)}>
          {buttonText} {buttonArrow && <span aria-hidden className='action-arrow icon icon-chevron-down ml-1 mt-1' />}
        </MenuButton>
      )}
      <MenuList role='listbox' className='rounded-sm border border-gray-400 py-0 shadow-md'>
        {showImpersonationMenu && AuthService.canImpersonate(session) &&
        <div className='py-3 w-80'>
          {ImpersonateMenuItem(session)}
          {EndImpersonationMenuItem(session)}
          <hr className='pt-3'/>
        </div>
        }

        {items.map(({ text, as = 'a', href, rel, target = '', onClick, hidden = true, ariaLabel }) => (
          hidden && <MenuLink
            role='option'
            as={as}
            href={href}
            rel={rel}
            target={target}
            key={text}
            className='font-base hover:text-blue-dark px-5 py-3 text-base hover:bg-transparent hover:underline'
            onClick={onClick}
            aria-label= {ariaLabel ?? (target === '_blank' ? `${text} (opens in new window)` : undefined)} 
          >
            {text}
          </MenuLink>
        ))}
      </MenuList>
    </Menu>
  )
}

const ImpersonateMenuItem = (session: Session) =>{
  return AuthService.canImpersonate(session) &&
    <div className='font-base px-5 py-0 text-sm'>
      <form onSubmit={e=>OnImpersonate(e, session)}>
        <div><label className='nhsuk-u-font-weight-bold' htmlFor='ImpersonateUserEmail'></label>Impersonate User</div>
        <div><input className='nhsuk-input text-sm' id='ImpersonateUserEmail' placeholder='Email address'/></div>
        <div>
          <button className='nhsuk-button nhsuk-button--warning my-2 w-full text-sm p-0.5' data-module="nhsuk-button" type='submit'>Impersonate</button>
        </div>
      </form>
    </div>
}

const EndImpersonationMenuItem = (session: Session) =>{
  return AuthService.isImpersonating(session) &&
    <div className='font-base px-5 py-0 text-sm'>
      <form onSubmit={EndImpersonation}>
        <div>
          <button className='nhsuk-button my-2 w-full text-sm p-0.5' data-module="nhsuk-button" type='submit'>End Impersonation</button>
        </div>
      </form>
    </div>
}

const GetCurrentDomain = () => {
  return window.location.hostname.includes('.nihr.ac.uk') ? '.nihr.ac.uk' : `.${window.location.hostname}`;
}

const OnImpersonate = (event: FormEvent, session: Session) =>{
  event.preventDefault();
  const email = (window.document.getElementById('ImpersonateUserEmail') as HTMLInputElement)?.value?.trim();

  if(email === session.auth.email){
    EndImpersonation(event);
    return;
  }

  if(email){
    Cookies.set('ImpersonateUserEmail', email, { path: '/', domain: GetCurrentDomain(), secure: true, sameSite: 'None' });
    sessionStorage.setItem("return-location", window.location.href);
    AuthService.clear();
  }else{
    // blank email maintains the current impersonation session.
  }

  window.location.replace(window.location.href);
}

const EndImpersonation = (event: FormEvent) =>{
  event.preventDefault();
  Cookies.remove('ImpersonateUserEmail', { path: '/', domain: GetCurrentDomain() });
  AuthService.clear();
  sessionStorage.setItem("return-location", window.location.href);
  window.location.replace(window.location.href);
}

export default DropdownMenu
